import React, { useState } from 'react';
import styled from '@emotion/styled';
import { FormValues } from 'components';

type AutoFill = {
  [key: string]: {
    [key: string]: { [key: string]: any };
  };
};

/* eslint-disable-next-line import/no-dynamic-require */
const autoFillData: AutoFill | null = process.env.REACT_APP_ENV !== 'prod' ? require('./autofill') : null;

const Container = styled.div`
  display: inline-block;
  height: 20px;
  position: absolute;
  right: 0;
  top: 0;
  overflow: visible;
  z-index: 10;
`;

const Select = styled.select<{ hide: boolean }>`
  display: inline-block;
  position: absolute;
  right: 0;
  top: 0;
  border-radius: 0;
  appearance: none;
  width: 20px;
  &:-moz-focusring {
    color: transparent;
    textshadow: 0 0 0 #000;
  }
  &:focus {
    outline: 0;
  }
  opacity: ${(props) => (props.hide ? '0' : '1')};
`;

const Button = styled.button`
  display: inline-block;
  width: 20px;
  height: 20px;
  appearance: none;
  border: 0;
  &:focus {
    outline: 0;
  }
  color: #ff9900;
  background-color: transparent;
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="512" viewBox="-2 0 511 512" width="512" fill="%23FF9900"><path d="m500.628906 311.332031-27.960937-16.144531c-30.160157-17.414062-30.1875-60.949219 0-78.375l27.960937-16.144531c7.171875-4.144531 9.628906-13.316407 5.488282-20.488281l-60.242188-104.355469c-4.136719-7.167969-13.316406-9.632813-20.492188-5.492188l-27.960937 16.144531c-30.15625 17.410157-67.863281-4.324218-67.863281-39.1875v-32.289062c0-8.285156-6.714844-15-15-15h-120.488282c-8.28125 0-15 6.714844-15 15v32.289062c0 34.828126-37.671874 56.617188-67.863281 39.1875l-27.960937-16.144531c-7.167969-4.140625-16.347656-1.683593-20.492188 5.492188l-60.242187 104.355469c-4.140625 7.171874-1.683594 16.347656 5.488281 20.488281l27.960938 16.144531c30.160156 17.414062 30.1875 60.949219 0 78.375l-27.957032 16.144531c-7.175781 4.144531-9.632812 13.316407-5.492187 20.488281l60.242187 104.355469c4.136719 7.167969 13.316406 9.636719 20.492188 5.492188l27.960937-16.144531c30.152344-17.414063 67.863281 4.320312 67.863281 39.1875v32.289062c0 8.285156 6.714844 15 15 15h120.484376c8.285156 0 15-6.714844 15-15v-32.289062c0-34.820313 37.671874-56.621094 67.867187-39.1875l27.960937 16.144531c7.167969 4.140625 16.347657 1.683593 20.488282-5.492188l60.246094-104.355469c4.140624-7.171874 1.683593-16.34375-5.488282-20.488281zm-73.238281 96.855469-14.96875-8.644531c-50.160156-28.964844-112.863281 7.195312-112.863281 65.167969v17.289062h-90.488282v-17.289062c0-57.921876-62.664062-94.15625-112.863281-65.167969l-14.96875 8.644531-45.246093-78.375 14.96875-8.644531c50.15625-28.957031 50.207031-101.347657.003906-130.335938l-14.972656-8.644531 45.246093-78.375 14.96875 8.644531c50.160157 28.964844 112.863281-7.195312 112.863281-65.167969v-17.289062h90.488282v17.289062c0 57.921876 62.660156 94.15625 112.863281 65.167969l14.96875-8.644531 45.246094 78.375-14.96875 8.644531c-50.15625 28.957031-50.207031 101.347657-.003907 130.335938l14.972657 8.644531zm0 0" /><path d="m254.316406 151c-57.894531 0-104.992187 47.101562-104.992187 105s47.097656 105 104.992187 105c57.890625 0 104.988282-47.101562 104.988282-105s-47.097657-105-104.988282-105zm0 180c-41.351562 0-74.992187-33.644531-74.992187-75s33.640625-75 74.992187-75c41.347656 0 74.988282 33.644531 74.988282 75s-33.640626 75-74.988282 75zm0 0" /></svg>');
  background-repeat: no-repeat;
  background-position: center;
  background-size: 16px 16px;
`;

const FEATURES = {
  HIDE_DEV_WIDGETS: false,
};

interface Props {
  id?: string;
  setFormValues: (arg: (values: FormValues) => FormValues) => void;
}

export const DevAutoFill: React.FC<Props> = ({ id, setFormValues }) => {
  const [active, setActive] = useState(false);

  if (!autoFillData?.autofill || FEATURES.HIDE_DEV_WIDGETS) {
    return null;
  }

  const data = id ? autoFillData.autofill[id] : null;

  if (!data) {
    return null;
  }

  const hasMultipleOptions = data.multiple;

  const handleOpenClick = () => {
    setActive(true);
  };

  const handleOnChange = (evt: React.ChangeEvent<HTMLSelectElement>) => {
    setActive(false);
    const { value } = evt.target;
    if (!value) {
      return;
    }
    if (value === 'autofill') {
      return setFormValues(() => data);
    }

    setFormValues(() => data[value]);
  };

  const options = hasMultipleOptions
    ? Object.keys(data).reduce<string[]>((acc, key) => (key === 'multiple' ? acc : [...acc, key]), [])
    : ['autofill'];

  return (
    <Container>
      <Button onClick={handleOpenClick} />
      <Select id={`autofill-${id}`} onChange={handleOnChange} hide={!active}>
        <option value="">-</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </Select>
    </Container>
  );
};
